import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Switch,
    Modal,
    Backdrop,
    Fade,
    RadioGroup,
    FormControlLabel,
    Radio,
    OutlinedInput,
    CircularProgress,
    FormHelperText,
    IconButton,
    useTheme,
    alpha
} from "@mui/material";
import { PhotoCamera, Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchStartupProfile, updateStartupProfile } from '../../../store/startup/profile/profileSlice';
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SingleFileUpload from "../../fileUpload/FileUpload";
import { Input } from "postcss";
import { getFileLink } from "../../../utils/getFileLink";
import businessModelsOptions from "../../../assets/json/businessModels.json"
import businessStages from "../../../assets/json/businessStages.json";
import businessSectors from "../../../assets/json/sectors.json";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import cities from "../../../assets/json/cities.json";



const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const CampaignProfile = () => {
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { profile, loading, error } = useSelector(state => state.profile || {});

    const [mainLogoPath, setMainLogoPath] = useState(null);
    const [name, setName] = useState("");
    const [promotionName, setPromotionName] = useState("");
    const [shortDescription, setShortDescription] = useState("");
    const [aboutStartup, setAboutStartup] = useState("");
    const [purposeSummary, setPurposeSummary] = useState("");
    const [purpose, setPurpose] = useState("");
    const [stage, setStage] = useState("");
    const [mainCategories, setMainCategories] = useState([]);
    const [businessModels, setBusinessModels] = useState([]);
    const [sectors, setSectors] = useState([]);
    const [fundedBefore, setFundedBefore] = useState(false);
    const [locations, setLocations] = useState([]);

    const theme = useTheme();
    // Validation state'i
    const [errors, setErrors] = useState({});

    const categoryOptions = ["Teknoloji, Üretim"];
    const locationOptions = [
        { name: "Türkiye (Genel)" },
        { name: "Global" },
        ...cities
    ].map(location => location.name);

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    const quillFormats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet'
    ];

    useEffect(() => {
        dispatch(fetchStartupProfile(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        if (profile) {
            setMainLogoPath(profile.mainLogoPath || null);
            setName(profile.name || "");
            setPromotionName(profile.promotionName || "");
            setShortDescription(profile.shortDescription || "");
            setAboutStartup(profile.aboutStartup || "");
            setPurposeSummary(profile.purposeSummary || "");
            setPurpose(profile.purpose || "");
            setStage(profile.stage || "");
            setMainCategories(profile.mainCategories || []);
            setBusinessModels(profile.businessModels || []);
            setSectors(profile.sectors || []);
            setFundedBefore(profile.fundedBefore || false);
            setLocations(profile.locations || []);
        }
    }, [profile]);

    // Validation rules
    const validationRules = {
        name: {
            required: true,
            minLength: 3,
            message: {
                required: 'Girişim adı zorunludur',
                minLength: 'Girişim adı en az 3 karakter olmalıdır'
            }
        },
        promotionName: {
            required: true,
            minLength: 3,
            message: {
                required: 'Kampanya adı zorunludur',
                minLength: 'Kampanya adı en az 3 karakter olmalıdır'
            }
        },
        shortDescription: {
            required: true,
            minLength: 10,
            message: {
                required: 'Kısa açıklama zorunludur',
                minLength: 'Kısa açıklama en az 10 karakter olmalıdır'
            }
        },
        aboutStartup: {
            required: true,
            minLength: 50,
            message: {
                required: 'Girişim hakkında bilgi zorunludur',
                minLength: 'Girişim hakkında bilgi en az 50 karakter olmalıdır'
            }
        },
        mainCategories: {
            required: true,
            message: {
                required: 'En az bir kategori seçilmelidir'
            }
        },
        businessModels: {
            required: true,
            message: {
                required: 'En az bir iş modeli seçilmelidir'
            }
        },
        stage: {
            required: true,
            message: {
                required: 'Girişim aşaması seçilmelidir'
            }
        },
        sectors: {
            required: true,
            message: {
                required: 'En az bir sektör seçilmelidir'
            }
        }
    };

    // Validate single field
    const validateField = (field, value) => {
        const rules = validationRules[field];
        if (!rules) return '';

        if (rules.required && (!value || (Array.isArray(value) && !value.length))) {
            return rules.message.required;
        }

        if (rules.minLength && value.length < rules.minLength) {
            return rules.message.minLength;
        }

        return '';
    };

    // Generic change handler
    const handleChange = (field, setValue) => (event) => {
        const value = event.target.value;
        setValue(value);

        setErrors(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Lütfen tüm zorunlu alanları doldurun');
            return;
        }

        try {
            const profileData = {
                mainLogoPath,
                name,
                promotionName,
                shortDescription,
                aboutStartup,
                purposeSummary,
                purpose,
                stage,
                locations,
                mainCategories,
                businessModels,
                sectors,
                fundedBefore
            };

            await dispatch(updateStartupProfile({
                startupId,
                profileData
            })).unwrap();

            toast.success('İşlem başarıyla gerçekleştirildi');
        } catch (error) {
            toast.error(error.message || 'Profil güncellenirken bir hata oluştu');
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Zorunlu alanların kontrolü
        if (!name?.trim()) {
            newErrors.name = 'Girişim adı zorunludur';
        }

        if (!promotionName?.trim()) {
            newErrors.promotionName = 'Kampanya adı zorunludur';
        }

        if (!shortDescription?.trim()) {
            newErrors.shortDescription = 'Kısa açıklama zorunludur';
        } else if (shortDescription.length > 200) {
            newErrors.shortDescription = 'Kısa açıklama 200 karakterden uzun olamaz';
        }

        if (!aboutStartup?.trim()) {
            newErrors.aboutStartup = 'Proje hakkında bilgi zorunludur';
        }

        if (!purposeSummary?.trim()) {
            newErrors.purposeSummary = 'Kapsam, amaç ve konu bilgisi zorunludur';
        }

        if (!stage) {
            newErrors.stage = 'Aşama seçimi zorunludur';
        }

        if (!Array.isArray(mainCategories) || mainCategories.length === 0) {
            newErrors.mainCategories = 'En az bir kategori seçilmelidir';
        }

        if (!Array.isArray(businessModels) || businessModels.length === 0) {
            newErrors.businessModels = 'En az bir iş modeli seçilmelidir';
        }

        if (!Array.isArray(sectors) || sectors.length === 0) {
            newErrors.sectors = 'En az bir sektör seçilmelidir';
        }

        console.log(newErrors);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogoUpload = (filePath) => {
        setMainLogoPath(filePath);
        console.log(mainLogoPath)
    }

    return (
        <Box sx={{ margin: "0 auto", maxWidth: 900 }}>
            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {/* Üst kısım - Fotoğraf ve İsim alanları */}
                    <Box sx={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                        {/* Sol taraf - Fotoğraf yükleme */}
                        <Box sx={{ width: '300px' }}>
                            <InputLabel sx={{ marginBottom: 1 }}>Projenin Kapak Fotoğrafı</InputLabel>
                            <SingleFileUpload onUploadComplete={handleLogoUpload} existFilePath={mainLogoPath} fileUploadPath={"startup"} />
                        </Box>

                        {/* Sağ taraf - İsim alanları */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Kampanyanın Adı"
                                fullWidth
                                required
                                value={promotionName}
                                onChange={handleChange('promotionName', setPromotionName)}
                                error={!!errors.promotionName}
                                helperText={errors.promotionName}
                            />
                            <TextField
                                label="Girişimin Adı"
                                fullWidth
                                required
                                value={name}
                                onChange={handleChange('name', setName)}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                            <TextField
                                label="Kampanyanı bir cümle ile anlat"
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ marginBottom: 2 }}
                                value={shortDescription}
                                onChange={handleChange('shortDescription', setShortDescription)}
                                error={!!errors.shortDescription}
                                helperText={errors.shortDescription}
                            />
                        </Box>
                    </Box>

                    {/* Diğer form alanları */}
                    <Box sx={{
                        mb: 3,
                        margin: "0 auto",
                        maxWidth: 900,
                        '& .quill': {
                            '& .ql-toolbar': {
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                borderTopLeftRadius: theme.shape.borderRadius,
                                borderTopRightRadius: theme.shape.borderRadius,
                                backgroundColor: theme.palette.mode === 'light'
                                    ? alpha(theme.palette.grey[100], 0.5)
                                    : alpha(theme.palette.grey[800], 0.5),
                                '& .ql-stroke': {
                                    stroke: theme.palette.mode === 'light'
                                        ? theme.palette.grey[700]
                                        : theme.palette.grey[300]
                                },
                                '& .ql-fill': {
                                    fill: theme.palette.mode === 'light'
                                        ? theme.palette.grey[700]
                                        : theme.palette.grey[300]
                                },
                                '& .ql-picker': {
                                    color: theme.palette.text.primary
                                },
                                '& .ql-picker-options': {
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                    boxShadow: theme.shadows[3]
                                }
                            },
                            '& .ql-container': {
                                border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
                                borderBottomLeftRadius: theme.shape.borderRadius,
                                borderBottomRightRadius: theme.shape.borderRadius,
                                backgroundColor: theme.palette.background.paper
                            },
                            '& .ql-editor': {
                                minHeight: '200px',
                                fontFamily: theme.typography.fontFamily,
                                fontSize: theme.typography.body1.fontSize,
                                color: theme.palette.text.primary,
                                '&.ql-blank::before': {
                                    color: theme.palette.text.disabled,
                                    fontStyle: 'normal',
                                    content: 'attr(data-placeholder)'
                                }
                            }
                        }
                    }}>
                        <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Proje Hakkında
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={aboutStartup}
                            onChange={setAboutStartup}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Proje Hakkında"

                        />
                        </Box>
                        

                        <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                            Kapsam, Amaç ve Konusu (Özet)
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={purposeSummary}
                            onChange={setPurposeSummary}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Kapsam, Amaç ve Konusu (Özet)"

                        />

                        <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                            Kapsam, Amaç ve Konusu
                        </Typography>
                        <ReactQuill
                            theme="snow"
                            value={purpose}
                            onChange={setPurpose}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Kapsam, Amaç ve Konusu"

                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, marginBottom: 2, marginTop: 3 }}>
                        <FormControl fullWidth error={!!errors.mainCategories}>
                            <InputLabel>Girişimin Ana Kategorileri *</InputLabel>
                            <Select
                                multiple
                                value={mainCategories}
                                onChange={(e) =>
                                    setMainCategories(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                                input={<OutlinedInput />}
                                MenuProps={MenuProps}
                            >
                                {categoryOptions.map((cat) => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.mainCategories && (
                                <FormHelperText>{errors.mainCategories}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl fullWidth error={!!errors.businessModels}>
                            <InputLabel>İş Modelleri *</InputLabel>
                            <Select
                                multiple
                                value={businessModels}
                                onChange={(e) =>
                                    setBusinessModels(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                                input={<OutlinedInput />}
                                MenuProps={MenuProps}
                            >
                                {businessModelsOptions.map((model) => (
                                    <MenuItem key={model.label} value={model.label}>
                                        {model.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.businessModels && (
                                <FormHelperText>{errors.businessModels}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, marginBottom: 2 }}>
                        <FormControl fullWidth error={!!errors.stage}>
                            <InputLabel>Girişimin Aşaması *</InputLabel>
                            <Select
                                value={stage}
                                onChange={(e) => setStage(e.target.value)}
                            >
                                {businessStages.map((item) => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.stage && (
                                <FormHelperText>{errors.stage}</FormHelperText>
                            )}
                        </FormControl>

                        <FormControl fullWidth error={!!errors.sectors}>
                            <InputLabel>Sektörler *</InputLabel>
                            <Select
                                multiple
                                value={sectors}
                                onChange={(e) =>
                                    setSectors(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                                input={<OutlinedInput />}
                                MenuProps={MenuProps}
                            >
                                {businessSectors.map((sector) => (
                                    <MenuItem key={sector.label} value={sector.label}>
                                        {sector.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.sectors && (
                                <FormHelperText>{errors.sectors}</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    <FormControl fullWidth sx={{ marginBottom: 2 }}>
                        <InputLabel>Girişimin Lokasyonları</InputLabel>
                        <Select
                            multiple
                            value={locations}
                            onChange={(e) => setLocations(typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
                            input={<OutlinedInput />}
                            MenuProps={{
                                ...MenuProps,
                                PaperProps: {
                                    ...MenuProps.PaperProps,
                                    style: {
                                        ...MenuProps.PaperProps.style,
                                        maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP, // Increased height for better visibility
                                    },
                                },
                            }}
                        >
                            {locationOptions.map((loc) => (
                                <MenuItem key={loc} value={loc}>
                                    {loc}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.locations && (
                            <FormHelperText error>{errors.locations}</FormHelperText>
                        )}
                    </FormControl>
                    <Box sx={{ marginTop: 4, textAlign: "right" }}>
                        <Button color="primary" variant="contained" onClick={handleSave}>Kaydet</Button>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default CampaignProfile;
